import { useEffect, useRef } from 'react';
import { useEffectOnce } from 'react-use';

import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { WebSocketConversationStatusChangedInboundMessage } from 'middleware/webSocket';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

/**
 * An effect hook that subscribes to the WebSocket conversation events and takes
 * a callback that handles such an event.
 *
 * @param handler The callback function performed on each event.
 */
export const useConversationEventsEffect = (
  handler: (message: WebSocketConversationStatusChangedInboundMessage) => void,
): void => {
  const dispatch = useAppDispatch();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // Handles the WebSocket subscription.
  useEffectOnce(() => {
    dispatch(
      websocketActions.request({
        type: 'connection:subscribe',
        params: {
          type: 'conversation_events',
          events: [
            'created',
            'queued',
            'assigned',
            'unassigned',
            'closed',
            'pending',
          ],
        },
      }),
    );

    return () => {
      dispatch(
        websocketActions.request({
          type: 'connection:unsubscribe',
          params: {
            type: 'conversation_events',
            events: [
              'created',
              'queued',
              'assigned',
              'unassigned',
              'closed',
              'pending',
            ],
          },
        }),
      );
    };
  });

  // Handles the pubSub subscription.
  useEffectOnce(() => {
    const unsubscribe = pubSub.subscribe(
      'webSocketConversationStatusChanged',
      (data) => {
        handlerRef.current(data);
      },
    );

    return () => unsubscribe();
  });
};
